import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import type { SortDirection } from 'src/types/common';
import { buildURLQueryParams } from 'src/utils/helpers';

export type UseTableReturn = {
  dense: boolean;
  page: number;
  rowsPerPage: number;
  order: SortDirection;
  orderBy: string;
  orderType: string | undefined;
  /********/
  selected: string[];
  onSelectRow: (id: string) => void;
  onSelectAllRows: (checked: boolean, newSelecteds: string[]) => void;
  onSelectAllRowsConditional: (checked: boolean, newSelecteds: string[]) => void;
  /********/
  onResetPage: () => void;
  onSort: (id: string, type?: string) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDense: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdatePageDeleteRow: (totalRowsInPage: number) => void;
  onUpdatePageDeleteRows: (totalRowsInPage: number, totalRowsFiltered: number) => void;
  /********/
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setDense: React.Dispatch<React.SetStateAction<boolean>>;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  setOrder: React.Dispatch<React.SetStateAction<SortDirection>>;
};

export type UseTableProps = {
  defaultDense?: boolean;
  defaultOrderBy?: string;
  defaultSelected?: string[];
  defaultRowsPerPage?: number;
  defaultCurrentPage?: number;
  defaultOrder?: SortDirection;
};

export function useTable(props?: UseTableProps): UseTableReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const [dense, setDense] = useState(!!props?.defaultDense);

  const [page, setPage] = useState(props?.defaultCurrentPage ?? 0);

  const [orderBy, setOrderBy] = useState(props?.defaultOrderBy ?? 'name');

  const [rowsPerPage, setRowsPerPage] = useState(props?.defaultRowsPerPage ?? 5);

  const [order, setOrder] = useState<SortDirection>(props?.defaultOrder ?? 'asc');

  const [orderType, setOrderType] = useState<string | undefined>();

  const [selected, setSelected] = useState<string[]>(props?.defaultSelected ?? []);

  useEffect(() => {
    setOrderBy(searchParams.get('sortBy') || props?.defaultOrderBy || '');
    setOrder((searchParams.get('sortOrder') as SortDirection) || props?.defaultOrder || 'asc');
    setPage(parseInt(searchParams.get('page') || '1') - 1 || props?.defaultCurrentPage || 0);
  }, [searchParams]);

  const onSort = useCallback(
    (id: string, type?: string) => {
      const isAsc = orderBy === id && order === 'asc';
      const isDesc = orderBy === id && order === 'desc';

      if (id !== '') {
        const currentSortOrder = isAsc ? 'desc' : 'asc';
        const currentSortBy = isDesc ? props?.defaultOrderBy || '' : id;

        setOrder(currentSortOrder);
        setOrderBy(currentSortBy);
        setOrderType(type);

        if (currentSortBy != props?.defaultOrderBy || currentSortOrder != props.defaultOrder) {
          setSearchParams((prev) => {
            return {
              ...buildURLQueryParams(
                {
                  sortBy: currentSortBy,
                  sortOrder: currentSortOrder,
                },
                prev
              ),
            };
          });
        } else {
          setSearchParams((prev) => {
            return {
              ...buildURLQueryParams(
                {
                  sortBy: '',
                  sortOrder: '',
                },
                prev
              ),
            };
          });
        }
      }
    },
    [order, orderBy, searchParams]
  );

  const onSelectRow = useCallback(
    (inputValue: string) => {
      const newSelected = selected.includes(inputValue)
        ? selected.filter((value) => value !== inputValue)
        : [...selected, inputValue];

      setSelected(newSelected);
    },
    [selected]
  );

  const onChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  }, []);

  const onChangeDense = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  }, []);

  const onSelectAllRows = useCallback((checked: boolean, inputValue: string[]) => {
    if (checked) {
      setSelected(inputValue);
      return;
    }

    setSelected([]);
  }, []);

  const onSelectAllRowsConditional = useCallback((checked: boolean, inputValue: string[]) => {
    if (checked) {
      setSelected(inputValue);
      return;
    }

    setSelected(inputValue);
  }, []);

  const onChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
      setSearchParams((prev) => buildURLQueryParams({ page: newPage + 1 }, prev));
    },
    [setSearchParams]
  );

  const onResetPage = useCallback(() => {
    setPage(0);
  }, []);

  const onUpdatePageDeleteRow = useCallback(
    (totalRowsInPage: number) => {
      setSelected([]);
      if (page) {
        if (totalRowsInPage < 2) {
          setPage(page - 1);
        }
      }
    },
    [page]
  );

  const onUpdatePageDeleteRows = useCallback(
    (totalRowsInPage: number, totalRowsFiltered: number) => {
      const totalSelected = selected.length;

      setSelected([]);

      if (page) {
        if (totalSelected === totalRowsInPage) {
          setPage(page - 1);
        } else if (totalSelected === totalRowsFiltered) {
          setPage(0);
        } else if (totalSelected > totalRowsInPage) {
          const newPage = Math.ceil((totalRowsFiltered - totalSelected) / rowsPerPage) - 1;

          setPage(newPage);
        }
      }
    },
    [page, rowsPerPage, selected.length]
  );

  return {
    dense,
    order,
    page,
    orderBy,
    orderType,
    rowsPerPage,
    /********/
    selected,
    onSelectRow,
    onSelectAllRows,
    onSelectAllRowsConditional,
    /********/
    onSort,
    onChangePage,
    onChangeDense,
    onResetPage,
    onChangeRowsPerPage,
    onUpdatePageDeleteRow,
    onUpdatePageDeleteRows,
    /********/
    setPage,
    setDense,
    setOrder,
    setOrderBy,
    setSelected,
    setRowsPerPage,
  };
}
